import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Mobile-first bottom sheet for transferring platform points into
 * an operator wallet. Replaces the previous window.prompt + alert
 * flow which was the worst mobile UX in the app (no validation
 * preview, no quick-picks, native dialog clashes with our dark
 * theme, hostile on iOS).
 *
 * Pattern:
 *   - Backdrop tap closes (cancel)
 *   - Slide-up animated sheet at bottom of viewport
 *   - Quick-pick chips (10 / 50 / 100 / 全部) for thumb-friendly
 *     amount selection without keyboard
 *   - Live preview row: 平台 N → 經營者 N
 *   - Confirm button disabled until valid amount
 *   - On submit, parent runs the transfer + closes the sheet
 *
 * This is also the template for Phase B's affinity/gifting sheets —
 * keep the API generic enough to reuse: title + max + onConfirm.
 */
interface Props {
  open: boolean;
  onClose: () => void;
  /** Transfer target name (character or legacy operator), shown in the
   *  sheet header. v3 rewrite uses the character name. */
  targetName: string;
  /** Currency name displayed alongside the target balance. Comes
   *  from the character's current operator (`operators.currencyName`)
   *  or defaults to "點" if no operator is bound. */
  currencyName: string;
  /** Optional currency icon URL (operator setting). */
  currencyIconUrl?: string;
  /** Current platform-side balance (max transferable). */
  platformBalance: number;
  /** Current target-side balance (for preview). */
  targetBalance: number;
  /** Returns a promise so the sheet can show a "transferring…" state. */
  onConfirm: (amount: number) => Promise<void>;
}

function CurrencyIcon({ url, size = 16, className = '' }: { url?: string; size?: number; className?: string }) {
  if (!url) return null;
  return (
    <img
      src={url}
      alt=""
      aria-hidden="true"
      className={`inline-block object-contain align-[-2px] ${className}`}
      style={{ width: size, height: size }}
      onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
    />
  );
}

export function TransferSheet({
  open,
  onClose,
  targetName,
  currencyName,
  currencyIconUrl,
  platformBalance,
  targetBalance,
  onConfirm,
}: Props) {
  const navigate = useNavigate();
  const [amountStr, setAmountStr] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setAmountStr(String(Math.min(50, platformBalance)));
      setErr(null);
      setSubmitting(false);
    }
  }, [open, platformBalance]);

  // Esc-to-close — sheet is modal, the keyboard equivalent of tapping
  // the backdrop. Gated on `submitting` so a mid-flight transfer can't
  // be aborted by an accidental key.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape' && !submitting) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, submitting, onClose]);

  if (!open) return null;

  const amount = Math.floor(Number(amountStr) || 0);
  const valid = amount > 0 && amount <= platformBalance && Number.isFinite(amount);
  const newPlatform = valid ? platformBalance - amount : platformBalance;
  const newTargetBalance = valid ? targetBalance + amount : targetBalance;

  const quickPicks: Array<{ label: string; value: number }> = [
    { label: '10', value: 10 },
    { label: '50', value: 50 },
    { label: '100', value: 100 },
    { label: '全部', value: platformBalance },
  ].filter((p) => p.value > 0 && p.value <= platformBalance);

  async function submit() {
    if (!valid) return;
    setSubmitting(true);
    setErr(null);
    try {
      await onConfirm(amount);
      onClose();
    } catch (e) {
      setErr(e instanceof Error ? e.message : '轉移失敗');
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-[90] flex items-end sm:items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="transfer-sheet-title"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={() => { if (!submitting) onClose(); }}
        aria-hidden="true"
      />
      {/* Sheet */}
      <div
        className="relative w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl bg-slate-900 border border-slate-700 shadow-2xl p-5 space-y-4"
        style={{ paddingBottom: `calc(1.25rem + env(safe-area-inset-bottom))` }}
      >
        {/* Drag handle (mobile decoration) */}
        <div className="sm:hidden absolute top-2 inset-x-0 flex justify-center pointer-events-none">
          <div className="h-1 w-10 rounded-full bg-slate-700" />
        </div>

        <header className="mt-2 sm:mt-0">
          <h2 id="transfer-sheet-title" className="text-lg font-semibold text-slate-100">
            轉入「{targetName}」
          </h2>
          <p className="text-xs text-slate-400 mt-0.5">
            單向轉移 · 無法退回 · 1 平台點 = 1 {currencyName}
            <CurrencyIcon url={currencyIconUrl} size={14} className="ml-1" />
          </p>
        </header>

        {platformBalance <= 0 && (
          <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-3 space-y-2">
            <div className="text-sm text-amber-100">平台沒有可轉移的黑耀石。</div>
            <div className="text-[11px] text-amber-200/80 leading-relaxed">
              先到錢包頁挑一個 ECPay 儲值方案買點數，回來再轉入給「{targetName}」。
            </div>
            <button
              type="button"
              onClick={() => { onClose(); navigate('/wallet'); }}
              className="w-full rounded-lg bg-amber-400 hover:bg-amber-300 text-slate-900 text-sm font-semibold py-2 transition"
            >
              前往儲值 →
            </button>
          </div>
        )}

        {/* Big amount input */}
        <div>
          <label htmlFor="transfer-amount" className="sr-only">轉入金額</label>
          <div className="relative">
            <input
              id="transfer-amount"
              type="number"
              inputMode="numeric"
              min={1}
              max={platformBalance}
              value={amountStr}
              onChange={(e) => setAmountStr(e.target.value)}
              autoFocus
              className="w-full text-center text-3xl font-bold tabular-nums bg-slate-950 border border-slate-700 rounded-xl py-3 text-slate-100 focus:outline-none focus:ring-2 focus:ring-violet-500"
            />
          </div>
          {/* Quick-picks */}
          <div className="mt-2 flex gap-2">
            {quickPicks.map((p) => (
              <button
                key={p.label}
                type="button"
                onClick={() => setAmountStr(String(p.value))}
                className="flex-1 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 text-sm py-2 transition"
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {/* Preview */}
        <div className="rounded-xl bg-slate-950/60 border border-slate-800 p-3 grid grid-cols-2 gap-2 text-sm">
          <div className="text-center">
            <div className="text-[10px] uppercase tracking-wider text-slate-500">平台 · 黑耀石</div>
            <div className="font-semibold tabular-nums text-slate-100 mt-0.5">
              {platformBalance.toLocaleString()}
              <span className="text-slate-500 text-xs mx-1">→</span>
              <span className={valid ? 'text-rose-300' : ''}>{newPlatform.toLocaleString()}</span>
            </div>
          </div>
          <div className="text-center">
            <div className="text-[10px] uppercase tracking-wider text-slate-500">
              {targetName} · {currencyName}
              <CurrencyIcon url={currencyIconUrl} size={12} className="ml-0.5" />
            </div>
            <div className="font-semibold tabular-nums text-slate-100 mt-0.5">
              {targetBalance.toLocaleString()}
              <span className="text-slate-500 text-xs mx-1">→</span>
              <span className={valid ? 'text-blue-300' : ''}>{newTargetBalance.toLocaleString()}</span>
            </div>
          </div>
        </div>

        {err && (
          <div role="alert" className="rounded-lg bg-rose-500/15 border border-rose-500/40 px-3 py-2 text-sm text-rose-200">
            {err}
          </div>
        )}

        {/* Action buttons */}
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="flex-1 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 py-3 font-medium transition disabled:opacity-50"
          >取消</button>
          <button
            type="button"
            onClick={() => { void submit(); }}
            disabled={!valid || submitting}
            className="flex-[2] rounded-lg bg-violet-500 hover:bg-violet-400 text-white py-3 font-medium transition disabled:bg-slate-700 disabled:text-slate-500"
          >
            {submitting ? '轉移中…' : `確認轉入 ${valid ? amount : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
}
